import { useLayoutEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getStatusOptionsArray } from "../../utils/utils";

// Bounds for the fitted root font-size (px). The search never returns a value
// outside this range, keeping text legible on tiny tiles and sane on huge ones.
const MIN_FONT = 5;
const MAX_FONT = 48;

// The content stops widening past this (px). On tiles wider than this — iPads —
// the content caps here and floats centred, rather than scaling up to fill the
// whole tile. This is the main knob for "how big can the text get".
const MAX_CONTENT_WIDTH = 120;

/**
 * Tutorial card rendered in the first grid cell of a collection. Teaches
 * tap-to-track and long-press-to-detail. Sized to fill whatever Box the caller
 * provides — the cells are a fixed 2:3 ratio.
 *
 * Sizing strategy (scale-to-fit): the inner content is measured against the
 * tile and we binary-search the largest root `font-size` whose rendered content
 * still fits in both dimensions. Every inner dimension (padding, gaps, icons) is
 * expressed in `em`, so that single font-size scales the whole card as one unit.
 * A ResizeObserver re-fits whenever the tile resizes. This guarantees the
 * content never overflows, regardless of tile width or how the status list grows.
 */
export default function CollectionTrackingHelper({ onClose }) {
  const theme = useTheme();
  const statusOptions = getStatusOptionsArray(theme).filter(
    (o) => o.value !== "none",
  );

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [fontSize, setFontSize] = useState(null);

  // Fit the content to the tile, re-running whenever the tile resizes or the
  // number of status rows changes (which alters the content's natural height).
  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const fit = () => {
      if (!container.clientWidth || !container.clientHeight) return;

      // Cap the fit region's width, then scale its height by the same factor so
      // the region keeps the tile's aspect ratio. On wide (iPad) tiles this
      // pins the font to what a MAX_CONTENT_WIDTH-wide tile would use, instead
      // of growing to fill the whole box.
      const availW = Math.min(container.clientWidth, MAX_CONTENT_WIDTH);
      const availH = container.clientHeight * (availW / container.clientWidth);

      // Largest font (to within 0.5px) whose content fits both dimensions.
      let lo = MIN_FONT;
      let hi = MAX_FONT;
      let best = MIN_FONT;
      while (hi - lo > 0.5) {
        const mid = (lo + hi) / 2;
        content.style.fontSize = `${mid}px`;
        const fits =
          content.scrollHeight <= availH && content.scrollWidth <= availW;
        if (fits) {
          best = mid;
          lo = mid;
        } else {
          hi = mid;
        }
      }

      // Hand the final size back to React; clear the probe style so the class
      // rule applied via sx (same value) isn't shadowed by a stale inline one.
      content.style.fontSize = "";
      setFontSize(best);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [statusOptions.length]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        border: `1px solid ${theme.palette.text.secondary}`,
        borderRadius: 1,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ position: "absolute", top: 2, right: 2, p: 0.5, zIndex: 1 }}
        aria-label="Dismiss tip"
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Measured, font-scaled content. Width fills the tile so text wraps at
          the real width; everything below is em-relative to this font-size. */}
      <Box
        ref={contentRef}
        sx={{
          width: "100%",
          maxWidth: `${MAX_CONTENT_WIDTH}px`,
          boxSizing: "border-box",
          px: "0.55em",
          py: "0.4em",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "0.5em",
          fontSize: fontSize ? `${fontSize}px` : `${MIN_FONT}px`,
          // Hide the first, pre-measurement paint to avoid a flash of unsized text.
          visibility: fontSize == null ? "hidden" : "visible",
        }}
      >
        <Typography
          color="text.secondary"
          sx={{ fontSize: "1em", lineHeight: 1.3, textWrap: "balance" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              verticalAlign: "middle",
              lineHeight: 0,
              mr: "0.4em",
              "& .MuiSvgIcon-root": {
                fontSize: "1.2em",
                transform: "translateY(-0.05em)",
              },
            }}
          >
            <InfoOutlinedIcon />
          </Box>
          <b>Tap</b> any cover to track:
        </Typography>

        <Stack alignSelf="stretch" sx={{ ml: "0.2em", gap: "0.2em" }}>
          {statusOptions.map((o) => (
            <Stack
              key={o.value}
              direction="row"
              alignItems="center"
              sx={{ gap: "0.4em" }}
            >
              <Box
                sx={{
                  display: "flex",
                  "& .MuiSvgIcon-root": { fontSize: "1.2em" },
                }}
              >
                {o.iconFilled}
              </Box>
              <Typography
                color="text.secondary"
                sx={{ fontSize: "1em", lineHeight: 1.2 }}
              >
                {o.label.toLowerCase()}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Typography
          color="text.secondary"
          sx={{ fontSize: "1em", lineHeight: 1.3, textWrap: "balance" }}
        >
          Or <b>long press</b> to see details
        </Typography>
      </Box>
    </Box>
  );
}
